<template>
  <div class="field">
    <label
      v-if="label"
      class="label"
    >
      {{ label }}
      <FieldHelp :text="help" />
    </label>
    <div class="control">
      <input
        :id="id"
        type="date"
        class="input"
        :name="id"
        :value="modelValue.value"
        :class="{ 'is-warning': hasWarning }"
        @input="handleInput"
      >
    </div>
    <p class="help">
      <span
        v-if="hasWarning"
        class="has-text-warning"
      >
        <font-awesome-icon
          icon="exclamation-triangle"
          class="warning-icon"
        />
        {{ modelValue.errorMessage }}
      </span>
    </p>
  </div>
</template>

<script>
import FieldHelp from './FieldHelp.vue';

export default {
  components: {
    FieldHelp,
  },
  props: {
    id: {
      type: String,
      default: 'field-date-default-id',
    },
    label: {
      type: String,
      default: '',
    },
    help: {
      type: String,
      default: '',
    },
    modelValue: {
      type: Object,
      default() {
        return { value: '', ok: true };
      },
    },
    vmax: {
      type: String,
      default: null,
    },
    vmin: {
      type: String,
      default: null,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    mustValidate() {
      // We do not want a field to show an error message due to missing
      // value on component mount, so input validation is only started
      // when told so by parent component, or upon user input.
      return this.validate || this.modelValue.modified;
    },
    hasWarning() {
      return this.mustValidate && !this.modelValue.ok;
    },
  },
  watch: {
    validate() {
      this.handleValidationChange();
    },
    vmin() {
      this.handleValidationChange();
    },
    vmax() {
      this.handleValidationChange();
    },
  },
  mounted() {
    // When the component is not mounted* and validation parameters change in the meantime,
    // watchers will not fire. So let's make sure the error state is consistent when
    // the component is shown again.
    // * for example when the user is on a different step of the wizard
    this.handleValidationChange();
  },
  methods: {
    handleInput(event) {
      const { value } = event.target;
      const errorMessage = this.check(value);
      const modified = true; // we are in handleInput so we know that value was modified by user
      const newModel = {
        value,
        ok: errorMessage == null,
        errorMessage,
        modified,
      };
      this.$emit('update:modelValue', newModel);
    },
    handleValidationChange() {
      const { value } = this.modelValue;
      const errorMessage = this.check(value);
      const modified = this.modelValue.modified === undefined ? false : this.modelValue.modified;
      const newModel = {
        value,
        ok: errorMessage == null,
        errorMessage,
        modified,
      };
      this.$emit('update:modelValue', newModel);
    },
    check(value) {
      // returns null if good, an error message otherwise
      if (value === '' || value == null) {
        if (this.optional) {
          return null;
        }
        return 'Missing value.';
      }
      if (this.vmin != null || this.vmax != null) {
        if ((this.vmin != null && value < this.vmin) || (this.vmax != null && value > this.vmax)) {
          return `Value must be in range of ${this.vmin || '-'} to ${this.vmax || '-'}.`;
        }
      }
      return null;
    },
  },
};
</script>
